import {
  Component,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  model,
  viewChild,
} from '@angular/core';

import { environment } from '../../../environments/environment';
import { PublicConfigService } from '../../services/public-config.service';
import { ThemeService } from '../../services/theme.service';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

/**
 * Compact Cloudflare Turnstile widget for admin public forms (login).
 * Site key from public-config (runtime) with environment fallback.
 * Empty key / disabled Turnstile uses a local bypass token.
 */
@Component({
  selector: 'app-turnstile',
  standalone: true,
  template: `
    <div class="my-1 min-h-[65px]">
      <div #host></div>
      @if (publicConfig.loaded() && !resolvedSiteKey()) {
        <p class="m-0 text-xs text-muted">Turnstile site key not configured (dev bypass).</p>
      }
    </div>
  `,
})
export class TurnstileComponent implements OnDestroy {
  readonly token = model('');
  private readonly themeService = inject(ThemeService);
  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  readonly publicConfig = inject(PublicConfigService);
  private widgetId: string | null = null;
  private rendered = false;

  constructor() {
    effect(() => {
      if (!this.publicConfig.loaded()) {
        return;
      }
      // Re-render when the app theme flips so the widget matches (Turnstile has no setTheme).
      const theme = this.themeService.resolved();
      if (this.widgetId && window.turnstile) {
        window.turnstile.remove(this.widgetId);
        this.widgetId = null;
        this.token.set('');
        this.rendered = false;
      }
      this.renderWidget(theme);
    });
  }

  resolvedSiteKey(): string {
    const runtime = this.publicConfig.config().turnstile_site_key;
    return runtime || environment.turnstileSiteKey;
  }

  reset(): void {
    if (this.widgetId && window.turnstile) {
      window.turnstile.reset(this.widgetId);
      this.token.set('');
      return;
    }
    if (!this.resolvedSiteKey()) {
      this.token.set('dev-bypass');
    }
  }

  ngOnDestroy(): void {
    if (this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  }

  private renderWidget(theme: 'light' | 'dark'): void {
    if (this.rendered) {
      return;
    }
    const siteKey = this.resolvedSiteKey();
    if (!siteKey) {
      this.token.set('dev-bypass');
      this.rendered = true;
      return;
    }
    this.rendered = true;
    void this.loadScript()
      .then(() => {
        if (!window.turnstile) {
          return;
        }
        this.widgetId = window.turnstile.render(this.host().nativeElement, {
          sitekey: siteKey,
          theme,
          size: 'compact',
          callback: (t: string) => this.token.set(t),
          'expired-callback': () => this.token.set(''),
          'error-callback': () => this.token.set(''),
        });
      })
      .catch(() => {
        this.token.set('');
      });
  }

  private loadScript(): Promise<void> {
    if (window.turnstile) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-mm-admin-turnstile]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.dataset['mmAdminTurnstile'] = '1';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Turnstile script failed'));
      document.head.appendChild(s);
    });
  }
}
